import React, { useState } from "react";
import { Card, CardContent } from "../../../../components/card";
import { Input } from "../../../../components/input";
import { Textarea } from "../../../../components/textarea";
import { Label } from "../../../../components/label";
import { Button } from "../../../../components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/select";
import PlusIcon from "../../../../assets/icons/plus-icon.svg";
import {
  BusinessCategory,
  BusinessProfileUIState,
  BusinessAddressUIState,
  Organization,
} from "../BusinessProfile.data";
import LocationPicker from "../../../../components/customs/map/LocationPicker";
import { reverseGeocode } from "../../../../services/LocationService";
import { useAuthSession } from "../../../../features/auth/shared/hooks/auth-session-context";

const partnerTypeOptions = [
  { value: "cities_municipalities", label: "Cities & Municipalities" },
  { value: "local_businesses", label: "Local Businesses" },
  {
    value: "nonprofits_community_organizations",
    label: "Nonprofits & Community Organizations",
  },
] as const;

interface BusinessInfoSectionProps {
  profile: BusinessProfileUIState;
  categories: BusinessCategory[];
  organizations: Organization[];
  onFieldChange: (field: keyof BusinessProfileUIState, value: any) => void;
  onAddressChange: (index: number, address: BusinessAddressUIState) => void;
  onAddAddress: () => void;
}

export const BusinessInfoSection: React.FC<BusinessInfoSectionProps> = ({
  profile,
  categories,
  organizations,
  onFieldChange,
  onAddressChange,
  onAddAddress,
}) => {
  const { user } = useAuthSession();
  // State to manage LocationPicker modal for each address
  const [openLocationPickers, setOpenLocationPickers] = useState<{ [key: number]: boolean }>({});

  // COMMENTED OUT: City dropdown functionality - can be reverted later if needed
  // Get unique cities from organizations
  // const cityOptions = organizations
  //     .map(org => org.name)
  //     .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
  //     .sort();

  // COMMENTED OUT: Helper function to get state from city
  // const getStateFromCity = (city: string): string => {
  //     const organization = organizations.find(org => org.name === city);
  //     return organization?.state || '';
  // };

  // COMMENTED OUT: Handle city change - automatically update state
  // const handleCityChange = (index: number, city: string) => {
  //     const address = profile.addresses[index];
  //     const state = getStateFromCity(city);
  //     onAddressChange(index, { ...address, city, state });
  // };

  // Handle location selection from LocationPicker
  // Updates address_1, city, state, zipcode, latitude, and longitude
  const handleLocationSelect = async (index: number, lat: number, lon: number, address: string) => {
    try {
      // Get detailed location information using reverse geocoding
      const geocodeResult = await reverseGeocode(lat, lon);
      const props = geocodeResult.properties;

      // Build address_1 from street address (street + name if available)
      // Priority: street > name > parse from full address
      let address_1 = "";
      if (props?.street) {
        address_1 = props.street;
        if (props.name && props.name !== props.street) {
          address_1 = `${props.name}, ${props.street}`;
        }
      } else if (props?.name) {
        address_1 = props.name;
      } else {
        // Fallback to the full address string, but try to remove city/state/zip
        address_1 = address.split(",").slice(0, -2).join(",").trim() || address;
      }

      // Extract city, state, and zipcode from properties
      const city = props?.city || "";
      const state = props?.state || "";
      const zipcode = props?.postcode || "";

      // Update address with all information from LocationPicker
      const currentAddress = profile.addresses[index];
      onAddressChange(index, {
        ...currentAddress,
        address_1,
        city,
        state,
        zipcode,
        latitude: lat,
        longitude: lon,
      });

      // Close the location picker
      setOpenLocationPickers({ ...openLocationPickers, [index]: false });
    } catch (error) {
      console.error("Error getting location details:", error);
      // Fallback: just update with basic info
      const currentAddress = profile.addresses[index];
      onAddressChange(index, {
        ...currentAddress,
        address_1: address,
        latitude: lat,
        longitude: lon,
      });
      setOpenLocationPickers({ ...openLocationPickers, [index]: false });
    }
  };

  // Get display address for LocationPicker input (full address including city, state, zipcode)
  const getDisplayAddress = (address: BusinessAddressUIState): string => {
    const parts = [address.address_1, address.city, address.state, address.zipcode].filter(Boolean);
    return parts.join(", ") || "";
  };
  return (
    <Card className="border border-[#e2e8f0] dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.3)] rounded-[8px]">
      <CardContent className="px-[24px] py-[24.8px]">
        <h2 className="text-[24px] font-semibold text-[#020817] dark:text-white tracking-[-0.6px] mb-6">
          Business Information
        </h2>

        <div className="space-y-[20px]">
          {/* First Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
            <div className="space-y-[13px]">
              <Label
                htmlFor="business_name"
                className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
              >
                Business Name
              </Label>
              <Input
                id="business_name"
                value={profile.business_name}
                onChange={(e) => onFieldChange("business_name", e.target.value)}
                className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground"
                placeholder="Enter business name"
              />
            </div>

            <div className="space-y-[13px]">
              <Label
                htmlFor="business_short_intro"
                className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
              >
                Business Short Intro
              </Label>
              <Input
                id="business_short_intro"
                value={profile.business_short_intro}
                onChange={(e) => onFieldChange("business_short_intro", e.target.value)}
                className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground"
                placeholder="Enter short introduction"
              />
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
            <div className="space-y-[13px]">
              <Label
                htmlFor="business_category"
                className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
              >
                Business Category
              </Label>
              <Select
                value={profile.business_category}
                onValueChange={(value) => onFieldChange("business_category", value)}
              >
                <SelectTrigger className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem
                      key={category.id}
                      value={category.id}
                    >
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-[13px]">
              <Label
                htmlFor="partner_type"
                className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
              >
                Partner Type
              </Label>
              <Select
                value={profile.partner_type}
                onValueChange={(value) => onFieldChange("partner_type", value)}
              >
                <SelectTrigger
                  id="partner_type"
                  className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground"
                >
                  <SelectValue placeholder="Select partner type" />
                </SelectTrigger>
                <SelectContent>
                  {partnerTypeOptions.map((partnerType) => (
                    <SelectItem key={partnerType.value} value={partnerType.value}>
                      {partnerType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-[13px]">
            <div className="space-y-[13px]">
              <Label
                htmlFor="business_url"
                className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
              >
                Business URL
              </Label>
              <Input
                id="business_url"
                value={profile.business_url}
                onChange={(e) => onFieldChange("business_url", e.target.value)}
                className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground"
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-[13px]">
            <Label
              htmlFor="description"
              className="text-[14px] font-medium text-[#020817] dark:text-gray-200"
            >
              Description
            </Label>
            <Textarea
              id="description"
              value={profile.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              className="h-[91px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] resize-none bg-white dark:bg-gray-900 text-foreground"
              placeholder="Enter business description"
            />
          </div>

          {/* Divider */}
          <div className="h-[17px]"></div>

          {/* Address Fields */}
          {profile.addresses.map((address, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-4 gap-[16px]"
            >
              <div className="space-y-[13px]">
                <Label className="text-[14px] font-medium text-[#020817] dark:text-gray-200">
                  Address
                </Label>
                <Input
                  value={getDisplayAddress(address)}
                  onClick={() => setOpenLocationPickers({ ...openLocationPickers, [index]: true })}
                  className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground cursor-pointer"
                  placeholder="Click to select location"
                  readOnly
                />
                <LocationPicker
                  isOpen={openLocationPickers[index] || false}
                  onClose={() => setOpenLocationPickers({ ...openLocationPickers, [index]: false })}
                  lat={address.latitude}
                  lon={address.longitude}
                  address={getDisplayAddress(address)}
                  onSelect={(lat, lon, addr) => handleLocationSelect(index, lat, lon, addr)}
                  restrictToUSA={
                    !["johnnguyen.it.39@gmail.com", "internal.support@civicpoints.io"].includes(
                      user?.email || "",
                    )
                  }
                />
              </div>

              <div className="space-y-[13px]">
                <Label className="text-[14px] font-medium text-[#020817] dark:text-gray-200">
                  City
                </Label>
                <Input
                  value={address.city}
                  readOnly
                  className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed text-foreground"
                  placeholder="Auto-filled from location"
                />
                {/* COMMENTED OUT: City dropdown - can be reverted later if needed
                                <Select
                                    value={address.city}
                                    onValueChange={(value) => handleCityChange(index, value)}
                                >
                                    <SelectTrigger className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-white dark:bg-gray-900 text-foreground">
                                        <SelectValue placeholder="Select city" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cityOptions.map((city) => (
                                            <SelectItem key={city} value={city}>
                                                {city}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                */}
              </div>

              <div className="space-y-[13px]">
                <Label className="text-[14px] font-medium text-[#020817] dark:text-gray-200">
                  State
                </Label>
                <Input
                  value={address.state}
                  readOnly
                  className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed text-foreground"
                  placeholder="Auto-filled from location"
                />
              </div>

              <div className="space-y-[13px]">
                <Label className="text-[14px] font-medium text-[#020817] dark:text-gray-200">
                  Zipcode
                </Label>
                <Input
                  value={address.zipcode}
                  readOnly
                  className="h-[40px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] px-[12.8px] py-[10.4px] text-[14px] bg-gray-50 dark:bg-gray-900/50 cursor-not-allowed text-foreground"
                  placeholder="Auto-filled from location"
                />
              </div>
            </div>
          ))}

          {/* Add Another Address Button */}
          <Button
            variant="outline"
            onClick={onAddAddress}
            className="w-full h-auto py-[15px] px-[25px] bg-[#d3f5ee] dark:bg-emerald-900/30 border-[#004232] dark:border-emerald-500 border-[1.4px] border-dashed rounded-[10px] hover:bg-[#c0ede3] dark:hover:bg-emerald-900/40 gap-[7px] transition-colors"
          >
            <img
              src={PlusIcon}
              alt="Add"
              className="w-5 h-5 dark:invert dark:opacity-80"
            />
            <span className="text-[16px] font-bold text-[#004232] dark:text-emerald-400 leading-normal">
              Add Another Address
            </span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
